/**
 * client/cosmetics.ts
 * Responsabilidad: Definición y dibujado procedimental de accesorios cosméticos modulares en capas.
 * Catálogo expandido a 50 cosméticos exclusivos (10 ojos, 10 bocas, 10 gafas, 10 tatuajes, 10 auras).
 * Renderizado en Canvas2D para Studio, Tarjetas y Previsualizador con soporte elemental completo.
 */

export type EyeStyle =
    | "default"
    | "anime"
    | "sharingan"
    | "starry"
    | "cyclops"
    | "laser"
    | "hypno"
    | "cat"
    | "demon"
    | "void";

export type MouthStyle =
    | "none"
    | "smile"
    | "fangs"
    | "uwu"
    | "stitched"
    | "smirk"
    | "mustache"
    | "grill"
    | "vampire"
    | "mask";

export type GlassesStyle =
    | "none"
    | "sunglasses"
    | "cyber"
    | "monocle"
    | "goggles"
    | "matrix"
    | "heart"
    | "nerd"
    | "nightvision"
    | "vr";

export type TattooStyle =
    | "none"
    | "runes"
    | "cracks"
    | "tribal"
    | "dragon"
    | "circuit"
    | "flame_mark"
    | "celestial"
    | "curse"
    | "alchemic_circle";

export type AuraStyle =
    | "none"
    | "crown"
    | "horns"
    | "spikes"
    | "phoenix"
    | "halo"
    | "orbital_orbs"
    | "electric_storm"
    | "abyssal_tendrils"
    | "cosmic_nova";

export type ThrusterStyle =
    | "none"
    | "alchemical"
    | "sakura"
    | "cosmic"
    | "runic"
    | "bubbles"
    | "musical"
    | "gold_coins"
    | "ghost_fire"
    | "rainbow";

export type CosmeticCategory = "eyes" | "mouth" | "glasses" | "tattoo" | "aura" | "thruster";

export interface ElementalPalette {
    primary: string;
    secondary: string;
    highlight: string;
    primaryHex: number;
    secondaryHex: number;
    highlightHex: number;
}

export function getElementalPalette(element: string = "water"): ElementalPalette {
    switch (element?.toLowerCase()) {
        case "fire":
            return { primary: "#dc2626", secondary: "#f97316", highlight: "#fde047", primaryHex: 0xdc2626, secondaryHex: 0xf97316, highlightHex: 0xfde047 };
        case "plant":
            return { primary: "#15803d", secondary: "#22c55e", highlight: "#86efac", primaryHex: 0x15803d, secondaryHex: 0x22c55e, highlightHex: 0x86efac };
        case "electric":
            return { primary: "#ca8a04", secondary: "#facc15", highlight: "#fef08a", primaryHex: 0xca8a04, secondaryHex: 0xfacc15, highlightHex: 0xfef08a };
        case "earth":
            return { primary: "#78350f", secondary: "#b45309", highlight: "#f59e0b", primaryHex: 0x78350f, secondaryHex: 0xb45309, highlightHex: 0xf59e0b };
        case "water":
        default:
            return { primary: "#0284c7", secondary: "#38bdf8", highlight: "#bae6fd", primaryHex: 0x0284c7, secondaryHex: 0x38bdf8, highlightHex: 0xbae6fd };
    }
}

export interface PlayerCosmeticsConfig {
    eyes: EyeStyle;
    mouth: MouthStyle;
    glasses: GlassesStyle;
    tattoo: TattooStyle;
    aura: AuraStyle;
    thruster: ThrusterStyle;
    familiar?: string;
    elementSkins?: Partial<Record<"water" | "fire" | "plant" | "electric" | "earth", string>>;
}

export const DEFAULT_COSMETICS: PlayerCosmeticsConfig = {
    eyes: "default",
    mouth: "none",
    glasses: "none",
    tattoo: "none",
    aura: "none",
    thruster: "none",
    familiar: "none",
    elementSkins: {
        water: "agua_001",
        fire: "fuego_001",
        plant: "planta_001",
        electric: "electrico_001",
        earth: "tierra_001",
    },
};

export {
    COSMETIC_CATALOG,
    getCosmeticPrice,
    type CosmeticCatalogItem,
    type CosmeticCurrency,
} from "../src/shared/constants";

const EYE_LIST: EyeStyle[] = [
    "default",
    "anime",
    "sharingan",
    "starry",
    "cyclops",
    "laser",
    "hypno",
    "cat",
    "demon",
    "void",
];

const MOUTH_LIST: MouthStyle[] = [
    "none",
    "smile",
    "fangs",
    "uwu",
    "stitched",
    "smirk",
    "mustache",
    "grill",
    "vampire",
    "mask",
];

const GLASSES_LIST: GlassesStyle[] = [
    "none",
    "sunglasses",
    "cyber",
    "monocle",
    "goggles",
    "matrix",
    "heart",
    "nerd",
    "nightvision",
    "vr",
];

const TATTOO_LIST: TattooStyle[] = [
    "none",
    "runes",
    "cracks",
    "tribal",
    "dragon",
    "circuit",
    "flame_mark",
    "celestial",
    "curse",
    "alchemic_circle",
];

const AURA_LIST: AuraStyle[] = [
    "none",
    "crown",
    "horns",
    "spikes",
    "phoenix",
    "halo",
    "orbital_orbs",
    "electric_storm",
    "abyssal_tendrils",
    "cosmic_nova",
];

export const THRUSTER_LIST: readonly ThrusterStyle[] = [
    "none",
    "alchemical",
    "sakura",
    "cosmic",
    "runic",
    "bubbles",
    "musical",
    "gold_coins",
    "ghost_fire",
    "rainbow",
] as const;

export function sanitizeCosmetics(raw: unknown): PlayerCosmeticsConfig {
    if (!raw || typeof raw !== "object") return { ...DEFAULT_COSMETICS };
    const obj = raw as Record<string, unknown>;
    return {
        eyes: EYE_LIST.includes(obj.eyes as EyeStyle) ? (obj.eyes as EyeStyle) : DEFAULT_COSMETICS.eyes,
        mouth: MOUTH_LIST.includes(obj.mouth as MouthStyle) ? (obj.mouth as MouthStyle) : DEFAULT_COSMETICS.mouth,
        glasses: GLASSES_LIST.includes(obj.glasses as GlassesStyle) ? (obj.glasses as GlassesStyle) : DEFAULT_COSMETICS.glasses,
        tattoo: TATTOO_LIST.includes(obj.tattoo as TattooStyle) ? (obj.tattoo as TattooStyle) : DEFAULT_COSMETICS.tattoo,
        aura: AURA_LIST.includes(obj.aura as AuraStyle) ? (obj.aura as AuraStyle) : DEFAULT_COSMETICS.aura,
        thruster: (THRUSTER_LIST as readonly string[]).includes(obj.thruster as string)
            ? (obj.thruster as ThrusterStyle)
            : DEFAULT_COSMETICS.thruster,
        familiar: typeof obj.familiar === "string" && obj.familiar.trim().length > 0 ? obj.familiar.trim() : "none",
        elementSkins: (obj.elementSkins && typeof obj.elementSkins === "object") ? {
            water: typeof (obj.elementSkins as any).water === "string" ? (obj.elementSkins as any).water : "agua_001",
            fire: typeof (obj.elementSkins as any).fire === "string" ? (obj.elementSkins as any).fire : "fuego_001",
            plant: typeof (obj.elementSkins as any).plant === "string" ? (obj.elementSkins as any).plant : "planta_001",
            electric: typeof (obj.elementSkins as any).electric === "string" ? (obj.elementSkins as any).electric : "electrico_001",
            earth: typeof (obj.elementSkins as any).earth === "string" ? (obj.elementSkins as any).earth : "tierra_001",
        } : { ...DEFAULT_COSMETICS.elementSkins },
    };
}

/** Genera cosméticos deterministas seguros para bots o jugadores remotos */
export function getBotCosmetics(sessionId: string = "bot"): PlayerCosmeticsConfig {
    const str = String(sessionId || "bot");
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h) + str.charCodeAt(i);
        h |= 0;
    }
    const seed = Math.abs(h);
    return {
        eyes: EYE_LIST[seed % EYE_LIST.length],
        mouth: MOUTH_LIST[(seed >> 3) % MOUTH_LIST.length],
        glasses: (seed % 3 === 0) ? GLASSES_LIST[(seed >> 5) % GLASSES_LIST.length] : "none",
        tattoo: (seed % 2 === 0) ? TATTOO_LIST[(seed >> 7) % TATTOO_LIST.length] : "none",
        aura: (seed % 4 === 0) ? AURA_LIST[(seed >> 9) % AURA_LIST.length] : "none",
        thruster: (seed % 5 === 0) ? THRUSTER_LIST[(seed >> 11) % THRUSTER_LIST.length] : "none",
        familiar: "none",
    };
}

/**
 * Dibuja el cuerpo base de uno de los 5 elementos reales de la arena en Canvas2D:
 * - Agua: gotas fluidas perimetrales y burbujas internas translúcidas con brillo especular.
 * - Fuego: núcleo incandescente, lenguas solares curvadas lamiendo el borde y chispas.
 * - Planta: esfera bosque profundo, franjas dinámicas en vórtice y esporas flotantes.
 * - Rayo: núcleo solar brillante, aro de choque perimetral y relámpagos fractales en zigzag.
 * - Tierra: base de terracota umbría, placas tectónicas de obsidiana, grietas de magma y geodas de ámbar.
 */
export function drawElementalBody(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    elementType: string,
    nowMs: number
): void {
    if (!Number.isFinite(radius) || radius <= 2) return;
    ctx.save();
    ctx.translate(cx, cy);

    const el = (elementType || "water").toLowerCase();

    if (el === "fire") {
        // --- FUEGO: Corona de 18 lenguas de fuego ondulantes + núcleo incandescente ---
        const tongueCount = 18;
        const rValley = radius * 0.65;
        ctx.beginPath();
        for (let i = 0; i < tongueCount; i++) {
            const aBase = (i / tongueCount) * Math.PI * 2;
            const aNext = ((i + 1) / tongueCount) * Math.PI * 2;
            const aMid = (aBase + aNext) * 0.5;

            const flicker = Math.sin(nowMs * 0.009 + i * 2.1) * (radius * 0.04);
            const rTip = Math.min(radius * 0.98, radius * 0.90 + flicker);
            const curl = aMid + 0.08;

            const vx = Math.cos(aBase) * rValley;
            const vy = Math.sin(aBase) * rValley;
            const tx = Math.cos(curl) * rTip;
            const ty = Math.sin(curl) * rTip;

            const cpx1 = Math.cos(aBase + 0.05) * (rValley + (rTip - rValley) * 0.65);
            const cpy1 = Math.sin(aBase + 0.05) * (rValley + (rTip - rValley) * 0.65);

            if (i === 0) ctx.moveTo(vx, vy);
            ctx.quadraticCurveTo(cpx1, cpy1, tx, ty);

            const cpx2 = Math.cos(aNext - 0.02) * (rValley + (rTip - rValley) * 0.25);
            const cpy2 = Math.sin(aNext - 0.02) * (rValley + (rTip - rValley) * 0.25);
            const nx = Math.cos(aNext) * rValley;
            const ny = Math.sin(aNext) * rValley;
            ctx.quadraticCurveTo(cpx2, cpy2, nx, ny);
        }
        ctx.closePath();
        ctx.fillStyle = "#ea580c";
        ctx.fill();
        ctx.strokeStyle = "#dc2626";
        ctx.lineWidth = Math.max(1.2, radius * 0.035);
        ctx.stroke();

        // Capa intermedia de llamas amarillas
        const innerTongues = 12;
        const rInVal = radius * 0.50;
        ctx.beginPath();
        for (let j = 0; j < innerTongues; j++) {
            const aBase = (j / innerTongues) * Math.PI * 2;
            const aNext = ((j + 1) / innerTongues) * Math.PI * 2;
            const aMid = (aBase + aNext) * 0.5;
            const rTip = radius * 0.62 + Math.sin(nowMs * -0.01 + j * 2.5) * (radius * 0.03);

            const vx = Math.cos(aBase) * rInVal;
            const vy = Math.sin(aBase) * rInVal;
            const tx = Math.cos(aMid + 0.06) * rTip;
            const ty = Math.sin(aMid + 0.06) * rTip;

            if (j === 0) ctx.moveTo(vx, vy);
            ctx.quadraticCurveTo(vx * 1.1, vy * 1.1, tx, ty);
            const nx = Math.cos(aNext) * rInVal;
            const ny = Math.sin(aNext) * rInVal;
            ctx.quadraticCurveTo(nx * 1.1, ny * 1.1, nx, ny);
        }
        ctx.closePath();
        ctx.fillStyle = "#facc15";
        ctx.fill();

        // Núcleo incandescente central
        const coreGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, radius * 0.36);
        coreGrad.addColorStop(0, "#ffffff");
        coreGrad.addColorStop(0.6, "#fef08a");
        coreGrad.addColorStop(1, "rgba(250, 204, 21, 0.4)");
        ctx.fillStyle = coreGrad;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.36, 0, Math.PI * 2);
        ctx.fill();

        // Chispas de plasma
        for (let s = 0; s < 6; s++) {
            const t = ((nowMs * 0.002 + s * 0.16) % 1.0);
            const ang = (s * 1.04) + Math.sin(nowMs * 0.004 + s) * 0.5;
            const dist = radius * (0.4 + t * 0.45);
            ctx.fillStyle = t < 0.4 ? "#ffffff" : "#f59e0b";
            ctx.beginPath();
            ctx.arc(Math.cos(ang) * dist, Math.sin(ang) * dist, Math.max(1, radius * 0.035 * (1 - t)), 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (el === "plant") {
        // --- PLANTA: Esfera verde bosque + vórtice de hojas perimetrales ---
        const rGrad = ctx.createRadialGradient(0, 0, radius * 0.2, 0, 0, radius * 0.85);
        rGrad.addColorStop(0, "#16a34a");
        rGrad.addColorStop(0.5, "#15803d");
        rGrad.addColorStop(1, "#14532d");
        ctx.fillStyle = rGrad;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.82, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#166534";
        ctx.lineWidth = Math.max(1.5, radius * 0.04);
        ctx.stroke();

        // Hojas curvadas abrazando el perímetro
        const leafCount = 8;
        const rot = nowMs * 0.0008;
        ctx.fillStyle = "#22c55e";
        ctx.strokeStyle = "#14532d";
        ctx.lineWidth = 1;
        for (let i = 0; i < leafCount; i++) {
            const a = rot + (i / leafCount) * Math.PI * 2;
            const lx1 = Math.cos(a) * radius * 0.65;
            const ly1 = Math.sin(a) * radius * 0.65;
            const lx2 = Math.cos(a + 0.35) * radius * 1.02;
            const ly2 = Math.sin(a + 0.35) * radius * 1.02;
            const lx3 = Math.cos(a + 0.6) * radius * 0.72;
            const ly3 = Math.sin(a + 0.6) * radius * 0.72;

            ctx.beginPath();
            ctx.moveTo(lx1, ly1);
            ctx.quadraticCurveTo(Math.cos(a + 0.2) * radius * 0.95, Math.sin(a + 0.2) * radius * 0.95, lx2, ly2);
            ctx.quadraticCurveTo(Math.cos(a + 0.45) * radius * 0.85, Math.sin(a + 0.45) * radius * 0.85, lx3, ly3);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }

        // Esporas bio-luminiscentes
        for (let s = 0; s < 5; s++) {
            const sAng = nowMs * 0.001 * (s % 2 === 0 ? 1 : -1) + (s * Math.PI * 2) / 5;
            const sDist = radius * (0.88 + Math.sin(nowMs * 0.003 + s) * 0.08);
            ctx.fillStyle = "#86efac";
            ctx.beginPath();
            ctx.arc(Math.cos(sAng) * sDist, Math.sin(sAng) * sDist, Math.max(1.2, radius * 0.04), 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (el === "electric") {
        // --- RAYO: Núcleo solar dorado + aro de choque + relámpagos fractales ---
        const haloPulse = 0.82 + 0.18 * Math.sin(nowMs * 0.012);
        const eGrad = ctx.createRadialGradient(0, 0, radius * 0.15, 0, 0, radius * 0.78);
        eGrad.addColorStop(0, "#ffffff");
        eGrad.addColorStop(0.35, "#fef08a");
        eGrad.addColorStop(0.7, "#facc15");
        eGrad.addColorStop(1, "#f59e0b");
        ctx.fillStyle = eGrad;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.75, 0, Math.PI * 2);
        ctx.fill();

        // Aro de choque eléctrico
        ctx.strokeStyle = `rgba(254, 240, 138, ${haloPulse})`;
        ctx.lineWidth = Math.max(1.8, radius * 0.05);
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.78, 0, Math.PI * 2);
        ctx.stroke();

        // Relámpagos fractales en zigzag
        const boltCount = 10;
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = Math.max(1.2, radius * 0.038);
        ctx.lineCap = "round";
        for (let b = 0; b < boltCount; b++) {
            const baseAng = (b / boltCount) * Math.PI * 2 + Math.sin(nowMs * 0.005 + b) * 0.15;
            const reach = radius * (0.85 + Math.sin(nowMs * 0.008 + b * 3) * 0.1);
            let curX = Math.cos(baseAng) * radius * 0.25;
            let curY = Math.sin(baseAng) * radius * 0.25;

            ctx.beginPath();
            ctx.moveTo(curX, curY);
            const segs = 3;
            for (let s = 1; s <= segs; s++) {
                const frac = s / segs;
                const jitter = (s === 2 ? 0.25 : -0.15) * Math.sin(nowMs * 0.02 + b * 7 + s);
                const sAng = baseAng + jitter;
                const dist = radius * 0.25 + frac * (reach - radius * 0.25);
                const nx = Math.cos(sAng) * dist;
                const ny = Math.sin(sAng) * dist;
                ctx.lineTo(nx, ny);
            }
            ctx.stroke();
        }
    } else if (el === "earth") {
        // --- TIERRA: Base de terracota umbría + placas de obsidiana + geodas ámbar ---
        const earthGrad = ctx.createRadialGradient(0, 0, radius * 0.2, 0, 0, radius * 0.85);
        earthGrad.addColorStop(0, "#b45309");
        earthGrad.addColorStop(0.6, "#78350f");
        earthGrad.addColorStop(1, "#5c2c16");
        ctx.fillStyle = earthGrad;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.82, 0, Math.PI * 2);
        ctx.fill();

        // Placas tectónicas de obsidiana
        const rot = nowMs * 0.00035;
        const plateCount = 6;
        ctx.fillStyle = "#1c1917";
        ctx.strokeStyle = "#5c2c16";
        ctx.lineWidth = Math.max(1, radius * 0.03);
        for (let i = 0; i < plateCount; i++) {
            const ang = rot + (i / plateCount) * Math.PI * 2;
            const dist = radius * 0.48;
            const px = Math.cos(ang) * dist;
            const py = Math.sin(ang) * dist;
            const pSize = radius * 0.22;

            ctx.beginPath();
            ctx.moveTo(px - pSize * 0.8, py - pSize * 0.4);
            ctx.lineTo(px + pSize * 0.3, py - pSize * 0.8);
            ctx.lineTo(px + pSize * 0.8, py - pSize * 0.1);
            ctx.lineTo(px + pSize * 0.4, py + pSize * 0.7);
            ctx.lineTo(px - pSize * 0.6, py + pSize * 0.5);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }

        // Grietas de magma ámbar
        ctx.strokeStyle = "#f59e0b";
        ctx.lineWidth = Math.max(1.2, radius * 0.035);
        for (let k = 0; k < 4; k++) {
            const kAng = rot * 1.5 + (k * Math.PI * 2) / 4;
            ctx.beginPath();
            ctx.moveTo(Math.cos(kAng) * radius * 0.2, Math.sin(kAng) * radius * 0.2);
            ctx.lineTo(Math.cos(kAng + 0.3) * radius * 0.72, Math.sin(kAng + 0.3) * radius * 0.72);
            ctx.stroke();
        }

        // Cristales de ámbar/bronce en órbita
        for (let c = 0; c < 4; c++) {
            const cAng = nowMs * 0.0008 * (c % 2 === 0 ? 1 : -1) + (c * Math.PI * 2) / 4;
            const cx = Math.cos(cAng) * radius * 0.96;
            const cy = Math.sin(cAng) * radius * 0.96;
            const cSize = Math.max(2, radius * 0.075);
            ctx.fillStyle = c % 2 === 0 ? "#d97706" : "#b45309";
            ctx.strokeStyle = "#fde047";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(cx, cy - cSize);
            ctx.lineTo(cx + cSize * 0.7, cy);
            ctx.lineTo(cx, cy + cSize);
            ctx.lineTo(cx - cSize * 0.7, cy);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }
    } else {
        // --- AGUA (POR DEFECTO): Esfera marina + burbujas translúcidas + gotas orbitales ---
        const waterGrad = ctx.createRadialGradient(-radius * 0.2, -radius * 0.2, radius * 0.1, 0, 0, radius * 0.88);
        waterGrad.addColorStop(0, "#7dd3fc");
        waterGrad.addColorStop(0.35, "#38bdf8");
        waterGrad.addColorStop(0.8, "#0284c7");
        waterGrad.addColorStop(1, "#0369a1");
        ctx.fillStyle = waterGrad;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.85, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(186, 230, 253, 0.75)";
        ctx.lineWidth = Math.max(1.5, radius * 0.04);
        ctx.stroke();

        // Burbujas internas translúcidas con reflejo especular en medialuna
        const bubbleCount = 4;
        for (let b = 0; b < bubbleCount; b++) {
            const bAng = (b / bubbleCount) * Math.PI * 2 + nowMs * 0.0005;
            const bDist = radius * (0.28 + (b % 3) * 0.14);
            const bx = Math.cos(bAng) * bDist;
            const by = Math.sin(bAng) * bDist;
            const bR = Math.max(2, radius * (0.09 + (b % 2) * 0.04));

            ctx.fillStyle = "rgba(125, 211, 252, 0.55)";
            ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(bx, by, bR, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();

            // Reflejo especular blanco
            ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
            ctx.beginPath();
            ctx.arc(bx - bR * 0.35, by - bR * 0.35, bR * 0.3, 0, Math.PI * 2);
            ctx.fill();
        }

        // Gotas orbitales elípticas en el perímetro
        const dropCount = 8;
        for (let d = 0; d < dropCount; d++) {
            const dAng = nowMs * 0.0009 + (d / dropCount) * Math.PI * 2;
            const dDist = radius * (0.98 + Math.sin(nowMs * 0.003 + d * 2) * 0.04);
            const dx = Math.cos(dAng) * dDist;
            const dy = Math.sin(dAng) * dDist;
            const dropR = Math.max(1.8, radius * 0.05);

            ctx.fillStyle = "#38bdf8";
            ctx.strokeStyle = "#7dd3fc";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(dx, dy, dropR, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();

            // Punto de luz en la gota
            ctx.fillStyle = "#ffffff";
            ctx.beginPath();
            ctx.arc(dx - dropR * 0.3, dy - dropR * 0.3, dropR * 0.35, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    ctx.restore();
}

/** 1. Capa de Tatuajes Rúnicos y Místicos */
export function drawTattooLayer(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    tattoo: TattooStyle,
    nowMs: number
): void {
    if (!Number.isFinite(radius) || radius <= 1) return;
    if (!tattoo || tattoo === "none") return;
    ctx.save();
    ctx.translate(x, y);

    if (tattoo === "runes") {
        const pulse = 0.55 + Math.sin(nowMs * 0.004) * 0.35;
        ctx.strokeStyle = `rgba(56, 189, 248, ${pulse})`;
        ctx.lineWidth = Math.max(1.5, radius * 0.06);
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.62, 0, Math.PI * 2);
        ctx.stroke();

        for (let i = 0; i < 4; i++) {
            const ang = (i * Math.PI) / 2 + nowMs * 0.0008;
            ctx.beginPath();
            ctx.moveTo(Math.cos(ang) * radius * 0.45, Math.sin(ang) * radius * 0.45);
            ctx.lineTo(Math.cos(ang) * radius * 0.78, Math.sin(ang) * radius * 0.78);
            ctx.stroke();
        }
    } else if (tattoo === "cracks") {
        ctx.strokeStyle = "rgba(15, 23, 42, 0.75)";
        ctx.lineWidth = Math.max(1.8, radius * 0.07);
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(-radius * 0.45, -radius * 0.45);
        ctx.lineTo(-radius * 0.1, -radius * 0.15);
        ctx.lineTo(-radius * 0.35, radius * 0.25);
        ctx.moveTo(radius * 0.4, -radius * 0.3);
        ctx.lineTo(radius * 0.12, radius * 0.1);
        ctx.lineTo(radius * 0.38, radius * 0.45);
        ctx.stroke();
    } else if (tattoo === "tribal") {
        ctx.strokeStyle = "rgba(244, 114, 182, 0.75)";
        ctx.lineWidth = Math.max(1.8, radius * 0.07);
        ctx.beginPath();
        ctx.arc(-radius * 0.35, 0, radius * 0.28, -Math.PI * 0.5, Math.PI * 0.5);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(radius * 0.35, 0, radius * 0.28, Math.PI * 0.5, Math.PI * 1.5);
        ctx.stroke();
    } else if (tattoo === "dragon") {
        // Tatuaje de Dragón Oriental Serpenteante 🐉
        ctx.strokeStyle = "rgba(245, 158, 11, 0.85)";
        ctx.lineWidth = Math.max(1.8, radius * 0.065);
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(-radius * 0.45, radius * 0.4);
        ctx.quadraticCurveTo(-radius * 0.5, -radius * 0.2, 0, -radius * 0.35);
        ctx.quadraticCurveTo(radius * 0.5, -radius * 0.45, radius * 0.35, 0.1 * radius);
        ctx.quadraticCurveTo(radius * 0.2, 0.45 * radius, -0.1 * radius, 0.3 * radius);
        ctx.stroke();

        // Garras y cabeza de dragón
        ctx.fillStyle = "#ef4444";
        ctx.beginPath();
        ctx.arc(-radius * 0.45, radius * 0.4, Math.max(2, radius * 0.08), 0, Math.PI * 2);
        ctx.fill();
    } else if (tattoo === "circuit") {
        // Bio-Circuito Cibernético Neón ⚡
        const pulse = 0.7 + 0.3 * Math.sin(nowMs * 0.005);
        ctx.strokeStyle = `rgba(34, 197, 94, ${pulse})`;
        ctx.lineWidth = Math.max(1.5, radius * 0.05);
        ctx.lineCap = "square";

        // Trazos a 90 grados
        ctx.beginPath();
        ctx.moveTo(-radius * 0.5, -radius * 0.2);
        ctx.lineTo(-radius * 0.2, -radius * 0.2);
        ctx.lineTo(-radius * 0.2, radius * 0.3);
        ctx.lineTo(radius * 0.3, radius * 0.3);
        ctx.lineTo(radius * 0.3, radius * 0.45);

        ctx.moveTo(radius * 0.4, -radius * 0.4);
        ctx.lineTo(radius * 0.1, -radius * 0.4);
        ctx.lineTo(radius * 0.1, -radius * 0.1);
        ctx.stroke();

        // Nodos circulares
        ctx.fillStyle = "#86efac";
        for (const [nx, ny] of [
            [-radius * 0.5, -radius * 0.2],
            [radius * 0.3, radius * 0.45],
            [radius * 0.4, -radius * 0.4],
        ]) {
            ctx.beginPath();
            ctx.arc(nx, ny, Math.max(2, radius * 0.055), 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (tattoo === "flame_mark") {
        // Marca Ígnea Tribal 🔥
        ctx.fillStyle = "rgba(239, 68, 68, 0.75)";
        ctx.strokeStyle = "rgba(245, 158, 11, 0.9)";
        ctx.lineWidth = Math.max(1.2, radius * 0.04);
        ctx.beginPath();
        ctx.moveTo(0, radius * 0.5);
        ctx.quadraticCurveTo(-radius * 0.35, radius * 0.2, -radius * 0.2, -radius * 0.3);
        ctx.quadraticCurveTo(-radius * 0.05, -radius * 0.05, 0, -radius * 0.48);
        ctx.quadraticCurveTo(radius * 0.05, -radius * 0.05, radius * 0.2, -radius * 0.3);
        ctx.quadraticCurveTo(radius * 0.35, radius * 0.2, 0, radius * 0.5);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    } else if (tattoo === "celestial") {
        // Constelación Estelar ✨
        ctx.strokeStyle = "rgba(192, 132, 252, 0.75)";
        ctx.lineWidth = Math.max(1.2, radius * 0.035);
        const stars = [
            [-radius * 0.35, -radius * 0.35],
            [-radius * 0.1, -radius * 0.45],
            [radius * 0.25, -radius * 0.3],
            [radius * 0.35, 0],
            [radius * 0.1, radius * 0.35],
            [-radius * 0.25, radius * 0.25],
        ];

        ctx.beginPath();
        ctx.moveTo(stars[0][0], stars[0][1]);
        for (let i = 1; i < stars.length; i++) {
            ctx.lineTo(stars[i][0], stars[i][1]);
        }
        ctx.stroke();

        ctx.fillStyle = "#ffffff";
        for (const [sx, sy] of stars) {
            ctx.beginPath();
            ctx.arc(sx, sy, Math.max(1.5, radius * 0.045), 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (tattoo === "curse") {
        // Sello Maldito Demoníaco (estilo hechicería oscura) 👹
        ctx.strokeStyle = "rgba(88, 28, 135, 0.85)";
        ctx.lineWidth = Math.max(1.8, radius * 0.065);
        ctx.lineCap = "round";

        // Marcas en ambas mejillas
        for (const s of [-1, 1]) {
            ctx.beginPath();
            ctx.moveTo(s * radius * 0.25, radius * 0.05);
            ctx.quadraticCurveTo(s * radius * 0.48, radius * 0.15, s * radius * 0.42, radius * 0.4);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(s * radius * 0.22, radius * 0.18);
            ctx.lineTo(s * radius * 0.48, radius * 0.25);
            ctx.stroke();
        }

        // Insignia en la frente
        ctx.fillStyle = "rgba(147, 51, 234, 0.9)";
        ctx.beginPath();
        ctx.arc(0, -radius * 0.42, Math.max(2, radius * 0.07), 0, Math.PI * 2);
        ctx.fill();
    } else if (tattoo === "alchemic_circle") {
        // Círculo Alquímico de Transmutación ⚗️
        const pulse = 0.65 + Math.sin(nowMs * 0.003) * 0.25;
        ctx.strokeStyle = `rgba(245, 158, 11, ${pulse})`;
        ctx.lineWidth = Math.max(1.2, radius * 0.04);

        // Doble círculo
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.58, 0, Math.PI * 2);
        ctx.arc(0, 0, radius * 0.46, 0, Math.PI * 2);
        ctx.stroke();

        // Triángulo y hexagrama alquímico
        ctx.beginPath();
        for (let i = 0; i < 3; i++) {
            const a = (i * Math.PI * 2) / 3 - Math.PI / 2;
            const tx = Math.cos(a) * radius * 0.46;
            const ty = Math.sin(a) * radius * 0.46;
            if (i === 0) ctx.moveTo(tx, ty);
            else ctx.lineTo(tx, ty);
        }
        ctx.closePath();
        ctx.stroke();

        ctx.beginPath();
        for (let i = 0; i < 3; i++) {
            const a = (i * Math.PI * 2) / 3 + Math.PI / 2;
            const tx = Math.cos(a) * radius * 0.46;
            const ty = Math.sin(a) * radius * 0.46;
            if (i === 0) ctx.moveTo(tx, ty);
            else ctx.lineTo(tx, ty);
        }
        ctx.closePath();
        ctx.stroke();
    }

    ctx.restore();
}

/** 2. Capa de Rostro (Ojos horizontales + Gafas + Boca) */
export function drawFaceLayer(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    eyes: EyeStyle,
    glasses: GlassesStyle,
    mouth: MouthStyle,
    facingAngle: number,
    nowMs: number
): void {
    if (!Number.isFinite(radius) || radius <= 1) return;
    ctx.save();
    ctx.translate(x, y);

    // Desplazamiento sutil hacia donde mira el avatar
    const lookX = Math.cos(facingAngle) * (radius * 0.10);
    const lookY = Math.sin(facingAngle) * (radius * 0.10);
    const eyeY = -radius * 0.10 + lookY * 0.5;
    const eyeSpacing = radius * 0.31;
    const eyeR = Math.max(2.5, radius * 0.19);

    // --- OJOS (10 Variantes) ---
    if (eyes === "cyclops") {
        const cx = lookX * 0.6;
        const cy = eyeY;
        const bigR = eyeR * 1.45;
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "rgba(0, 0, 0, 0.65)";
        ctx.lineWidth = Math.max(1.2, radius * 0.04);
        ctx.beginPath();
        ctx.arc(cx, cy, bigR, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = "#ef4444";
        ctx.beginPath();
        ctx.arc(cx + lookX * 0.5, cy + lookY * 0.5, bigR * 0.52, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "#000000";
        ctx.beginPath();
        ctx.arc(cx + lookX * 0.5, cy + lookY * 0.5, bigR * 0.25, 0, Math.PI * 2);
        ctx.fill();
    } else {
        for (const sign of [-1, 1]) {
            const ex = sign * eyeSpacing + lookX * 0.4;
            const ey = eyeY;

            ctx.save();
            ctx.translate(ex, ey);

            const px = Math.cos(facingAngle) * (eyeR * 0.36);
            const py = Math.sin(facingAngle) * (eyeR * 0.36);

            if (eyes === "laser") {
                // Ojos Cibernéticos con HUD y haz láser
                ctx.fillStyle = "#0f172a";
                ctx.strokeStyle = "#ef4444";
                ctx.lineWidth = Math.max(1.5, radius * 0.045);
                ctx.fillRect(-eyeR * 1.1, -eyeR * 0.9, eyeR * 2.2, eyeR * 1.8);
                ctx.strokeRect(-eyeR * 1.1, -eyeR * 0.9, eyeR * 2.2, eyeR * 1.8);

                // Retícula central
                ctx.fillStyle = "#ef4444";
                ctx.beginPath();
                ctx.arc(px, py, eyeR * 0.35, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = "#ffffff";
                ctx.beginPath();
                ctx.arc(px, py, eyeR * 0.15, 0, Math.PI * 2);
                ctx.fill();
            } else if (eyes === "hypno") {
                // Ojos Hipnóticos con espiral giratoria
                ctx.fillStyle = "#ffffff";
                ctx.strokeStyle = "#000000";
                ctx.lineWidth = 1.2;
                ctx.beginPath();
                ctx.arc(0, 0, eyeR, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();

                // Espirales
                const spin = nowMs * 0.005;
                ctx.strokeStyle = "#7c3aed";
                ctx.lineWidth = Math.max(1.2, eyeR * 0.18);
                ctx.beginPath();
                for (let a = 0; a < Math.PI * 4; a += 0.2) {
                    const r = (a / (Math.PI * 4)) * (eyeR * 0.85);
                    const sx = Math.cos(a + spin) * r;
                    const sy = Math.sin(a + spin) * r;
                    if (a === 0) ctx.moveTo(sx, sy);
                    else ctx.lineTo(sx, sy);
                }
                ctx.stroke();
            } else if (eyes === "cat") {
                // Ojos Felinos con pupila rasgada vertical
                ctx.fillStyle = "#facc15";
                ctx.strokeStyle = "#000000";
                ctx.lineWidth = 1.2;
                ctx.beginPath();
                ctx.arc(0, 0, eyeR, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();

                ctx.fillStyle = "#090d16";
                ctx.beginPath();
                ctx.ellipse(px * 0.6, py * 0.4, eyeR * 0.22, eyeR * 0.72, 0, 0, Math.PI * 2);
                ctx.fill();

                // Brillo
                ctx.fillStyle = "#ffffff";
                ctx.beginPath();
                ctx.arc(px * 0.6 - eyeR * 0.2, py * 0.4 - eyeR * 0.2, eyeR * 0.16, 0, Math.PI * 2);
                ctx.fill();
            } else if (eyes === "demon") {
                // Ojos Demoníacos con esclerótica negra y fuego rojo
                ctx.fillStyle = "#090d16";
                ctx.strokeStyle = "#dc2626";
                ctx.lineWidth = Math.max(1.2, radius * 0.04);
                ctx.beginPath();
                ctx.arc(0, 0, eyeR, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();

                ctx.fillStyle = "#ef4444";
                ctx.beginPath();
                ctx.arc(px, py, eyeR * 0.48, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = "#facc15";
                ctx.beginPath();
                ctx.ellipse(px, py, eyeR * 0.18, eyeR * 0.42, 0, 0, Math.PI * 2);
                ctx.fill();
            } else if (eyes === "void") {
                // Ojos del Vacío Cósmico
                ctx.fillStyle = "#030712";
                ctx.strokeStyle = "#8b5cf6";
                ctx.lineWidth = Math.max(1.2, radius * 0.04);
                ctx.beginPath();
                ctx.arc(0, 0, eyeR, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();

                const vPulse = 0.7 + 0.3 * Math.sin(nowMs * 0.006);
                ctx.fillStyle = `rgba(168, 85, 247, ${vPulse})`;
                ctx.beginPath();
                ctx.arc(px, py, eyeR * 0.38, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = "#ffffff";
                ctx.beginPath();
                ctx.arc(px, py, eyeR * 0.16, 0, Math.PI * 2);
                ctx.fill();
            } else {
                // Estilos base (default, anime, sharingan, starry)
                ctx.fillStyle = eyes === "sharingan" ? "#ef4444" : eyes === "starry" ? "#0f172a" : "#ffffff";
                ctx.strokeStyle = "rgba(0, 0, 0, 0.65)";
                ctx.lineWidth = Math.max(1.2, radius * 0.04);
                ctx.beginPath();
                if (eyes === "anime") {
                    ctx.ellipse(0, 0, eyeR * 1.1, eyeR * 0.82, 0, 0, Math.PI * 2);
                } else {
                    ctx.arc(0, 0, eyeR, 0, Math.PI * 2);
                }
                ctx.fill();
                ctx.stroke();

                if (eyes === "sharingan") {
                    ctx.strokeStyle = "#000000";
                    ctx.lineWidth = Math.max(1, eyeR * 0.12);
                    ctx.beginPath();
                    ctx.arc(0, 0, eyeR * 0.58, 0, Math.PI * 2);
                    ctx.stroke();
                    ctx.fillStyle = "#000000";
                    ctx.beginPath();
                    ctx.arc(px, py, eyeR * 0.28, 0, Math.PI * 2);
                    ctx.fill();
                } else if (eyes === "starry") {
                    ctx.fillStyle = "#fde047";
                    ctx.beginPath();
                    ctx.arc(px, py, eyeR * 0.42, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.fillStyle = "#ffffff";
                    ctx.beginPath();
                    ctx.arc(-eyeR * 0.25, -eyeR * 0.25, eyeR * 0.2, 0, Math.PI * 2);
                    ctx.fill();
                } else {
                    ctx.fillStyle = "#090d16";
                    ctx.beginPath();
                    ctx.arc(px, py, eyeR * 0.46, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.fillStyle = "#ffffff";
                    ctx.beginPath();
                    ctx.arc(px - eyeR * 0.16, py - eyeR * 0.16, eyeR * 0.18, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
            ctx.restore();
        }
    }

    // --- GAFAS (10 Variantes) ---
    if (glasses === "cyber") {
        const visorW = radius * 0.78;
        const visorH = Math.max(4, radius * 0.26);
        const vx = -visorW / 2 + lookX * 0.3;
        const vy = eyeY - visorH / 2;
        ctx.fillStyle = "rgba(56, 189, 248, 0.78)";
        ctx.strokeStyle = "#e0f2fe";
        ctx.lineWidth = Math.max(1.2, radius * 0.045);
        ctx.fillRect(vx, vy, visorW, visorH);
        ctx.strokeRect(vx, vy, visorW, visorH);
    } else if (glasses === "sunglasses") {
        ctx.fillStyle = "#090d16";
        ctx.strokeStyle = "#e2e8f0";
        ctx.lineWidth = Math.max(1.2, radius * 0.04);
        const lW = eyeR * 2.1;
        const lH = eyeR * 1.5;
        ctx.fillRect(-eyeSpacing - lW / 2 + lookX * 0.3, eyeY - lH / 2, lW, lH);
        ctx.strokeRect(-eyeSpacing - lW / 2 + lookX * 0.3, eyeY - lH / 2, lW, lH);
        ctx.fillRect(eyeSpacing - lW / 2 + lookX * 0.3, eyeY - lH / 2, lW, lH);
        ctx.strokeRect(eyeSpacing - lW / 2 + lookX * 0.3, eyeY - lH / 2, lW, lH);
        ctx.beginPath();
        ctx.moveTo(-eyeSpacing + lW / 2 + lookX * 0.3, eyeY);
        ctx.lineTo(eyeSpacing - lW / 2 + lookX * 0.3, eyeY);
        ctx.stroke();
    } else if (glasses === "monocle") {
        const mx = eyeSpacing + lookX * 0.4;
        ctx.strokeStyle = "#fbbf24";
        ctx.lineWidth = Math.max(1.8, radius * 0.065);
        ctx.beginPath();
        ctx.arc(mx, eyeY, eyeR * 1.28, 0, Math.PI * 2);
        ctx.stroke();
        ctx.lineWidth = Math.max(1, radius * 0.035);
        ctx.beginPath();
        ctx.moveTo(mx + eyeR * 1.1, eyeY + eyeR * 0.5);
        ctx.quadraticCurveTo(mx + eyeR * 1.2, eyeY + radius * 0.35, mx + eyeR * 0.4, eyeY + radius * 0.5);
        ctx.stroke();
    } else if (glasses === "goggles") {
        // Steampunk Goggles 🥽
        const gW = eyeR * 2.2;
        const gH = eyeR * 1.8;
        ctx.fillStyle = "#78350f";
        ctx.strokeStyle = "#d97706";
        ctx.lineWidth = Math.max(1.5, radius * 0.05);

        for (const sign of [-1, 1]) {
            const gx = sign * eyeSpacing + lookX * 0.3;
            ctx.beginPath();
            ctx.arc(gx, eyeY, gW * 0.55, 0, Math.PI * 2);
            ctx.fillStyle = "rgba(217, 119, 6, 0.45)";
            ctx.fill();
            ctx.stroke();
        }
        // Puente de cuero
        ctx.strokeStyle = "#78350f";
        ctx.lineWidth = Math.max(2, radius * 0.06);
        ctx.beginPath();
        ctx.moveTo(-eyeSpacing + gW * 0.4, eyeY);
        ctx.lineTo(eyeSpacing - gW * 0.4, eyeY);
        ctx.stroke();
    } else if (glasses === "matrix") {
        // Gafas Estilo Matrix 🕶️
        const mW = eyeR * 2.0;
        const mH = eyeR * 0.9;
        ctx.fillStyle = "#022c22";
        ctx.strokeStyle = "#10b981";
        ctx.lineWidth = Math.max(1.2, radius * 0.035);
        ctx.fillRect(-eyeSpacing - mW / 2 + lookX * 0.3, eyeY - mH / 2, mW, mH);
        ctx.strokeRect(-eyeSpacing - mW / 2 + lookX * 0.3, eyeY - mH / 2, mW, mH);
        ctx.fillRect(eyeSpacing - mW / 2 + lookX * 0.3, eyeY - mH / 2, mW, mH);
        ctx.strokeRect(eyeSpacing - mW / 2 + lookX * 0.3, eyeY - mH / 2, mW, mH);
        ctx.beginPath();
        ctx.moveTo(-eyeSpacing + mW / 2 + lookX * 0.3, eyeY);
        ctx.lineTo(eyeSpacing - mW / 2 + lookX * 0.3, eyeY);
        ctx.stroke();
    } else if (glasses === "heart") {
        // Gafas de Corazones Rosa/Rubí 💖
        ctx.fillStyle = "rgba(244, 63, 94, 0.75)";
        ctx.strokeStyle = "#fda4af";
        ctx.lineWidth = Math.max(1.2, radius * 0.04);
        for (const sign of [-1, 1]) {
            const hx = sign * eyeSpacing + lookX * 0.3;
            const hSize = eyeR * 1.1;
            ctx.beginPath();
            ctx.moveTo(hx, eyeY + hSize * 0.6);
            ctx.bezierCurveTo(hx - hSize, eyeY + hSize * 0.1, hx - hSize, eyeY - hSize * 0.6, hx, eyeY - hSize * 0.3);
            ctx.bezierCurveTo(hx + hSize, eyeY - hSize * 0.6, hx + hSize, eyeY + hSize * 0.1, hx, eyeY + hSize * 0.6);
            ctx.fill();
            ctx.stroke();
        }
    } else if (glasses === "nerd") {
        // Gafas de Profesor / Nerd 🤓
        ctx.strokeStyle = "#090d16";
        ctx.lineWidth = Math.max(2, radius * 0.06);
        ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
        for (const sign of [-1, 1]) {
            const nx = sign * eyeSpacing + lookX * 0.3;
            ctx.beginPath();
            ctx.arc(nx, eyeY, eyeR * 1.25, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
        }
        // Cinta blanca adhesiva en el centro
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(-radius * 0.07 + lookX * 0.3, eyeY - radius * 0.06, radius * 0.14, radius * 0.12);
    } else if (glasses === "nightvision") {
        // Visor de Visión Nocturna Fósforo Verde 🟢
        const nvW = eyeR * 1.9;
        const nvH = eyeR * 1.6;
        ctx.fillStyle = "#14532d";
        ctx.strokeStyle = "#22c55e";
        ctx.lineWidth = Math.max(1.5, radius * 0.05);
        for (const sign of [-1, 1]) {
            const nvx = sign * eyeSpacing + lookX * 0.3;
            ctx.beginPath();
            ctx.arc(nvx, eyeY, nvW * 0.55, 0, Math.PI * 2);
            ctx.fillStyle = "rgba(34, 197, 94, 0.65)";
            ctx.fill();
            ctx.stroke();
        }
        // Puente y cilindros
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(-radius * 0.12 + lookX * 0.3, eyeY - radius * 0.04, radius * 0.24, radius * 0.08);
    } else if (glasses === "vr") {
        // Visor de Realidad Virtual VR 🥽
        const vrW = radius * 0.84;
        const vrH = Math.max(6, radius * 0.34);
        const vrX = -vrW / 2 + lookX * 0.3;
        const vrY = eyeY - vrH / 2;

        ctx.fillStyle = "#1e293b";
        ctx.strokeStyle = "#64748b";
        ctx.lineWidth = Math.max(1.5, radius * 0.045);
        ctx.fillRect(vrX, vrY, vrW, vrH);
        ctx.strokeRect(vrX, vrY, vrW, vrH);

        // Barra LED Neón frontal
        const ledPulse = 0.8 + 0.2 * Math.sin(nowMs * 0.008);
        ctx.fillStyle = `rgba(56, 189, 248, ${ledPulse})`;
        ctx.fillRect(vrX + vrW * 0.15, eyeY - radius * 0.02, vrW * 0.7, Math.max(2, radius * 0.04));
    }

    // --- BOCA (10 Variantes) ---
    const mouthX = lookX * 0.4;
    const mouthY = radius * 0.24 + lookY * 0.3;
    ctx.strokeStyle = "#090d16";
    ctx.lineWidth = Math.max(1.6, radius * 0.065);
    ctx.lineCap = "round";

    if (mouth === "smile") {
        ctx.beginPath();
        ctx.arc(mouthX, mouthY - radius * 0.08, radius * 0.28, 0.18 * Math.PI, 0.82 * Math.PI);
        ctx.stroke();
    } else if (mouth === "fangs") {
        ctx.beginPath();
        ctx.arc(mouthX, mouthY - radius * 0.08, radius * 0.28, 0.18 * Math.PI, 0.82 * Math.PI);
        ctx.stroke();
        ctx.fillStyle = "#ffffff";
        for (const s of [-1, 1]) {
            const fx = mouthX + s * radius * 0.14;
            const fy = mouthY + radius * 0.08;
            ctx.beginPath();
            ctx.moveTo(fx - radius * 0.05, fy - radius * 0.02);
            ctx.lineTo(fx, fy + radius * 0.14);
            ctx.lineTo(fx + radius * 0.05, fy - radius * 0.02);
            ctx.closePath();
            ctx.fill();
        }
    } else if (mouth === "uwu") {
        const wR = radius * 0.11;
        ctx.beginPath();
        ctx.arc(mouthX - wR, mouthY, wR, 0, Math.PI);
        ctx.arc(mouthX + wR, mouthY, wR, 0, Math.PI);
        ctx.stroke();
    } else if (mouth === "stitched") {
        const w = radius * 0.26;
        ctx.beginPath();
        ctx.moveTo(mouthX - w, mouthY);
        ctx.lineTo(mouthX + w, mouthY);
        for (let t = -1; t <= 1; t++) {
            const tx = mouthX + t * (w * 0.6);
            ctx.moveTo(tx, mouthY - radius * 0.07);
            ctx.lineTo(tx, mouthY + radius * 0.07);
        }
        ctx.stroke();
    } else if (mouth === "smirk") {
        // Sonrisa Pícara Asimétrica 😏
        ctx.beginPath();
        ctx.moveTo(mouthX - radius * 0.2, mouthY + radius * 0.02);
        ctx.quadraticCurveTo(mouthX, mouthY + radius * 0.12, mouthX + radius * 0.26, mouthY - radius * 0.08);
        ctx.stroke();
    } else if (mouth === "mustache") {
        // Bigote de Caballero Curvado 🥸
        ctx.fillStyle = "#451a03";
        ctx.beginPath();
        ctx.moveTo(mouthX, mouthY - radius * 0.04);
        ctx.quadraticCurveTo(mouthX - radius * 0.18, mouthY - radius * 0.08, mouthX - radius * 0.32, mouthY + radius * 0.06);
        ctx.quadraticCurveTo(mouthX - radius * 0.22, mouthY + radius * 0.14, mouthX, mouthY + radius * 0.04);
        ctx.quadraticCurveTo(mouthX + radius * 0.22, mouthY + radius * 0.14, mouthX + radius * 0.32, mouthY + radius * 0.06);
        ctx.quadraticCurveTo(mouthX + radius * 0.18, mouthY - radius * 0.08, mouthX, mouthY - radius * 0.04);
        ctx.closePath();
        ctx.fill();
    } else if (mouth === "grill") {
        // Grillz de Oro Brillante ✨
        ctx.fillStyle = "#facc15";
        ctx.strokeStyle = "#b45309";
        ctx.lineWidth = Math.max(1.2, radius * 0.04);
        const gW = radius * 0.42;
        const gH = radius * 0.16;
        ctx.beginPath();
        ctx.roundRect(mouthX - gW / 2, mouthY, gW, gH, [4]);
        ctx.fill();
        ctx.stroke();

        // Destello de diamante
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(mouthX + radius * 0.1, mouthY + radius * 0.05, Math.max(1.5, radius * 0.04), 0, Math.PI * 2);
        ctx.fill();
    } else if (mouth === "vampire") {
        // Vampiro Sangriento 🧛
        ctx.strokeStyle = "#991b1b";
        ctx.lineWidth = Math.max(2, radius * 0.07);
        ctx.beginPath();
        ctx.arc(mouthX, mouthY - radius * 0.06, radius * 0.25, 0.15 * Math.PI, 0.85 * Math.PI);
        ctx.stroke();

        ctx.fillStyle = "#ffffff";
        for (const s of [-1, 1]) {
            const fx = mouthX + s * radius * 0.12;
            const fy = mouthY + radius * 0.08;
            ctx.beginPath();
            ctx.moveTo(fx - radius * 0.04, fy);
            ctx.lineTo(fx, fy + radius * 0.16);
            ctx.lineTo(fx + radius * 0.04, fy);
            ctx.closePath();
            ctx.fill();
        }

        // Gota de sangre
        ctx.fillStyle = "#dc2626";
        ctx.beginPath();
        ctx.arc(mouthX + radius * 0.12, mouthY + radius * 0.25, Math.max(1.2, radius * 0.035), 0, Math.PI * 2);
        ctx.fill();
    } else if (mouth === "mask") {
        // Máscara Ninja Shinobi 🥷
        const mW = radius * 0.65;
        const mH = radius * 0.38;
        ctx.fillStyle = "#0f172a";
        ctx.strokeStyle = "#334155";
        ctx.lineWidth = Math.max(1.5, radius * 0.045);
        ctx.beginPath();
        ctx.moveTo(mouthX - mW / 2, mouthY - radius * 0.06);
        ctx.lineTo(mouthX + mW / 2, mouthY - radius * 0.06);
        ctx.lineTo(mouthX + mW * 0.35, mouthY + mH);
        ctx.lineTo(mouthX - mW * 0.35, mouthY + mH);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    }

    ctx.restore();
    void nowMs;
}

/** 3. Capa de Aura / Corona / Halo / Elementos Místicos Cromoadaptativos */
export function drawAuraLayer(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    aura: AuraStyle,
    nowMs: number,
    element: string = "water"
): void {
    if (!Number.isFinite(radius) || radius <= 1) return;
    if (!aura || aura === "none") return;
    ctx.save();
    ctx.translate(x, y);

    const pal = getElementalPalette(element);

    if (aura === "crown") {
        const bob = Math.sin(nowMs * 0.005) * (radius * 0.04);
        const baseW = radius * 0.42;
        const baseY = -radius * 0.88 + bob;
        const topY = -radius * 1.28 + bob;

        ctx.fillStyle = "#fbbf24";
        ctx.strokeStyle = "#b45309";
        ctx.lineWidth = Math.max(1.2, radius * 0.045);
        ctx.beginPath();
        ctx.moveTo(-baseW, baseY);
        ctx.lineTo(-baseW * 1.1, topY + radius * 0.08);
        ctx.lineTo(-baseW * 0.45, baseY - radius * 0.12);
        ctx.lineTo(0, topY);
        ctx.lineTo(baseW * 0.45, baseY - radius * 0.12);
        ctx.lineTo(baseW * 1.1, topY + radius * 0.08);
        ctx.lineTo(baseW, baseY);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Gemas reales cromoadaptadas al elemento
        const gemR = Math.max(1.5, radius * 0.05);
        ctx.fillStyle = pal.secondary;
        ctx.strokeStyle = pal.highlight;
        ctx.lineWidth = 1;
        for (const gx of [-baseW * 0.7, 0, baseW * 0.7]) {
            ctx.beginPath();
            ctx.arc(gx, baseY - radius * 0.05, gemR, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
        }
    } else if (aura === "horns") {
        ctx.fillStyle = pal.primary;
        ctx.strokeStyle = pal.highlight;
        ctx.lineWidth = Math.max(1.2, radius * 0.045);
        for (const s of [-1, 1]) {
            ctx.beginPath();
            ctx.moveTo(s * radius * 0.35, -radius * 0.78);
            ctx.quadraticCurveTo(s * radius * 0.85, -radius * 1.25, s * radius * 0.48, -radius * 1.32);
            ctx.quadraticCurveTo(s * radius * 0.55, -radius * 0.95, s * radius * 0.62, -radius * 0.62);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }
    } else if (aura === "spikes") {
        ctx.fillStyle = pal.secondary;
        ctx.strokeStyle = pal.highlight;
        ctx.lineWidth = 1.2;
        const spikes = 8;
        for (let i = 0; i < spikes; i++) {
            const ang = (i / spikes) * Math.PI * 2 + nowMs * 0.001;
            const rInner = radius * 1.02;
            const rOuter = radius * 1.26;
            ctx.beginPath();
            ctx.moveTo(Math.cos(ang - 0.14) * rInner, Math.sin(ang - 0.14) * rInner);
            ctx.lineTo(Math.cos(ang) * rOuter, Math.sin(ang) * rOuter);
            ctx.lineTo(Math.cos(ang + 0.14) * rInner, Math.sin(ang + 0.14) * rInner);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }
    } else if (aura === "phoenix") {
        const flutter = Math.sin(nowMs * 0.008) * (radius * 0.08);

        // Alas exteriores naciendo estrictamente del perímetro exterior (r >= 1.02)
        // cromoadaptadas al elemento activo sin iluminar el interior
        for (const s of [-1, 1]) {
            ctx.fillStyle = pal.secondary;
            ctx.strokeStyle = pal.primary;
            ctx.lineWidth = Math.max(1.2, radius * 0.04);
            ctx.beginPath();
            ctx.moveTo(s * radius * 1.02, -radius * 0.2);
            ctx.quadraticCurveTo(s * radius * 1.48, -radius * 0.95 + flutter, s * radius * 1.38, -radius * 1.45 + flutter);
            ctx.quadraticCurveTo(s * radius * 1.15, -radius * 0.7, s * radius * 1.05, -radius * 0.05);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            ctx.fillStyle = pal.primary;
            ctx.beginPath();
            ctx.moveTo(s * radius * 1.03, -radius * 0.05);
            ctx.quadraticCurveTo(s * radius * 1.58, -radius * 0.35 + flutter * 0.7, s * radius * 1.48, -radius * 0.85 + flutter * 0.7);
            ctx.quadraticCurveTo(s * radius * 1.2, -radius * 0.4, s * radius * 1.05, 0.15 * radius);
            ctx.closePath();
            ctx.fill();

            ctx.fillStyle = pal.highlight;
            ctx.beginPath();
            ctx.moveTo(s * radius * 1.04, 0.1 * radius);
            ctx.quadraticCurveTo(s * radius * 1.38, 0.15 * radius + flutter * 0.5, s * radius * 1.28, -radius * 0.35 + flutter * 0.5);
            ctx.quadraticCurveTo(s * radius * 1.15, -radius * 0.1, s * radius * 1.05, 0.25 * radius);
            ctx.closePath();
            ctx.fill();
        }

        const crestBob = Math.sin(nowMs * 0.009) * (radius * 0.06);
        ctx.fillStyle = pal.secondary;
        ctx.strokeStyle = pal.highlight;
        ctx.lineWidth = Math.max(1, radius * 0.035);
        ctx.beginPath();
        ctx.moveTo(-radius * 0.2, -radius * 1.02);
        ctx.quadraticCurveTo(0, -radius * 1.5 + crestBob, 0, -radius * 1.55 + crestBob);
        ctx.quadraticCurveTo(0, -radius * 1.5 + crestBob, radius * 0.2, -radius * 1.02);
        ctx.quadraticCurveTo(0, -radius * 1.1, -radius * 0.2, -radius * 1.02);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    } else if (aura === "halo") {
        // Halo Sagrado Levitante cromoadaptativo 😇
        const bob = Math.sin(nowMs * 0.004) * (radius * 0.05);
        const haloY = -radius * 1.05 + bob;
        const hRadX = radius * 0.65;
        const hRadY = radius * 0.22;

        ctx.strokeStyle = pal.highlight;
        ctx.lineWidth = Math.max(3, radius * 0.09);
        ctx.beginPath();
        ctx.ellipse(0, haloY, hRadX, hRadY, 0, 0, Math.PI * 2);
        ctx.stroke();

        ctx.strokeStyle = pal.secondary;
        ctx.lineWidth = Math.max(1.8, radius * 0.05);
        ctx.beginPath();
        ctx.ellipse(0, haloY, hRadX, hRadY, 0, 0, Math.PI * 2);
        ctx.stroke();

        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = Math.max(1, radius * 0.02);
        ctx.beginPath();
        ctx.ellipse(0, haloY, hRadX, hRadY, 0, 0, Math.PI * 2);
        ctx.stroke();
    } else if (aura === "orbital_orbs") {
        // Orbes Esferas Místicas Orbitantes cromoadaptativas 🔮
        const orbColors = [pal.secondary, pal.primary, pal.highlight];
        for (let i = 0; i < 3; i++) {
            const ang = nowMs * 0.002 + (i * Math.PI * 2) / 3;
            const ox = Math.cos(ang) * (radius * 1.32);
            const oy = Math.sin(ang) * (radius * 0.68) - radius * 0.1;
            const oR = Math.max(2.5, radius * 0.12);

            ctx.fillStyle = orbColors[i];
            ctx.beginPath();
            ctx.arc(ox, oy, oR * 1.4, 0, Math.PI * 2);
            ctx.globalAlpha = 0.35;
            ctx.fill();
            ctx.globalAlpha = 1.0;

            ctx.fillStyle = orbColors[i];
            ctx.beginPath();
            ctx.arc(ox, oy, oR, 0, Math.PI * 2);
            ctx.fill();

            ctx.fillStyle = "#ffffff";
            ctx.beginPath();
            ctx.arc(ox - oR * 0.35, oy - oR * 0.35, oR * 0.4, 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (aura === "electric_storm") {
        // Tormenta de Rayos Eléctricos con tonos cromoadaptados ⚡
        const boltCount = 6;
        ctx.save();
        ctx.strokeStyle = pal.highlight;
        ctx.shadowColor = pal.secondary;
        ctx.shadowBlur = Math.max(3, radius * 0.08);
        ctx.lineWidth = Math.max(1.5, radius * 0.045);
        ctx.lineCap = "round";

        for (let b = 0; b < boltCount; b++) {
            const ang = (b / boltCount) * Math.PI * 2 + nowMs * 0.004;
            const r1 = radius * 1.05;
            const r2 = radius * 1.38 + Math.sin(nowMs * 0.01 + b) * (radius * 0.1);

            let cx = Math.cos(ang) * r1;
            let cy = Math.sin(ang) * r1;
            ctx.beginPath();
            ctx.moveTo(cx, cy);

            const midAng = ang + (b % 2 === 0 ? 0.2 : -0.2);
            const midR = (r1 + r2) * 0.5;
            ctx.lineTo(Math.cos(midAng) * midR, Math.sin(midAng) * midR);
            ctx.lineTo(Math.cos(ang) * r2, Math.sin(ang) * r2);
            ctx.stroke();
        }
        ctx.restore();
    } else if (aura === "abyssal_tendrils") {
        // Zarcillos Sombríos del Abismo cromoadaptativos 🐙
        const tendrils = 6;
        ctx.strokeStyle = pal.primary;
        ctx.lineWidth = Math.max(2, radius * 0.07);
        ctx.lineCap = "round";

        for (let t = 0; t < tendrils; t++) {
            const baseAng = (t / tendrils) * Math.PI * 2;
            const sway = Math.sin(nowMs * 0.004 + t * 1.5) * 0.4;
            const startX = Math.cos(baseAng) * (radius * 1.05);
            const startY = Math.sin(baseAng) * (radius * 1.05);
            const endX = Math.cos(baseAng + sway) * (radius * 1.45);
            const endY = Math.sin(baseAng + sway) * (radius * 1.45);

            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.quadraticCurveTo(
                Math.cos(baseAng + sway * 1.5) * (radius * 1.2),
                Math.sin(baseAng + sway * 1.5) * (radius * 1.2),
                endX,
                endY
            );
            ctx.stroke();

            ctx.fillStyle = pal.secondary;
            ctx.beginPath();
            ctx.arc(endX, endY, Math.max(1.5, radius * 0.04), 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (aura === "cosmic_nova") {
        // Vórtice Galáctico & Destellos Astrales Cromoadaptativos 🌌
        const rot = nowMs * 0.0012;
        const pulse = 1 + Math.sin(nowMs * 0.003) * 0.06;
        const novaR = radius * 1.38 * pulse;

        // 1. Doble anillo de acreción exterior ondulante
        ctx.strokeStyle = pal.secondary;
        ctx.lineWidth = Math.max(1.2, radius * 0.035);
        ctx.globalAlpha = 0.55;
        ctx.beginPath();
        ctx.arc(0, 0, novaR, 0, Math.PI * 2);
        ctx.stroke();

        ctx.strokeStyle = pal.highlight;
        ctx.lineWidth = Math.max(0.8, radius * 0.02);
        ctx.globalAlpha = 0.8;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 1.12 * pulse, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1.0;

        // 2. 4 Brazos espirales galácticos fluidos
        const arms = 4;
        ctx.strokeStyle = pal.primary;
        ctx.lineWidth = Math.max(1.8, radius * 0.05);
        ctx.lineCap = "round";
        for (let i = 0; i < arms; i++) {
            const baseAng = (i * Math.PI * 2) / arms + rot;
            const startR = radius * 1.06;
            const midR = radius * 1.25;
            const endR = radius * 1.44;
            const x1 = Math.cos(baseAng) * startR;
            const y1 = Math.sin(baseAng) * startR;
            const cpX = Math.cos(baseAng + 0.35) * midR;
            const cpY = Math.sin(baseAng + 0.35) * midR;
            const x2 = Math.cos(baseAng + 0.65) * endR;
            const y2 = Math.sin(baseAng + 0.65) * endR;

            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.quadraticCurveTo(cpX, cpY, x2, y2);
            ctx.stroke();

            // Núcleo brillante en la punta del brazo espiral
            ctx.fillStyle = pal.highlight;
            ctx.beginPath();
            ctx.arc(x2, y2, Math.max(1.5, radius * 0.035), 0, Math.PI * 2);
            ctx.fill();
        }

        // 3. Microestrellas centelleantes de 4 puntas (Diamantes astrales)
        for (let s = 0; s < 4; s++) {
            const sAng = (s * Math.PI * 2) / 4 - rot * 0.8;
            const sR = radius * (1.18 + Math.sin(nowMs * 0.005 + s) * 0.1);
            const sx = Math.cos(sAng) * sR;
            const sy = Math.sin(sAng) * sR;
            const starSize = Math.max(2, radius * 0.05) * (0.8 + Math.sin(nowMs * 0.008 + s * 2) * 0.3);

            ctx.fillStyle = pal.highlight;
            ctx.beginPath();
            ctx.moveTo(sx, sy - starSize);
            ctx.lineTo(sx + starSize * 0.3, sy);
            ctx.lineTo(sx, sy + starSize);
            ctx.lineTo(sx - starSize * 0.3, sy);
            ctx.closePath();
            ctx.fill();

            ctx.beginPath();
            ctx.moveTo(sx - starSize, sy);
            ctx.lineTo(sx, sy + starSize * 0.3);
            ctx.lineTo(sx + starSize, sy);
            ctx.lineTo(sx, sy - starSize * 0.3);
            ctx.closePath();
            ctx.fill();
        }
    }

    ctx.restore();
}

/** Dibuja la estela activa de un propulsor alquímico en Canvas2D para la vista previa del avatar */
export function drawThrusterPreview(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    thruster: ThrusterStyle,
    nowMs: number,
    element: string = "water"
): void {
    if (!thruster || thruster === "none" || !Number.isFinite(radius) || radius <= 2) return;
    const pal = getElementalPalette(element);
    ctx.save();
    ctx.translate(cx, cy);

    const baseOffsetY = radius * 0.95;

    if (thruster === "alchemical") {
        // Chorro de plasma pulsante con chispas adaptado al elemento
        const plumePulse = 1 + Math.sin(nowMs * 0.02) * 0.2;
        const plumeH = radius * 0.8 * plumePulse;
        const grad = ctx.createLinearGradient(0, baseOffsetY, 0, baseOffsetY + plumeH);
        grad.addColorStop(0, pal.highlight);
        grad.addColorStop(0.4, pal.secondary);
        grad.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(-radius * 0.35, baseOffsetY);
        ctx.quadraticCurveTo(0, baseOffsetY + plumeH * 1.2, radius * 0.35, baseOffsetY);
        ctx.closePath();
        ctx.fill();

        for (let i = 0; i < 4; i++) {
            const p = ((nowMs * 0.005 + i * 0.25) % 1.0);
            const sx = Math.sin(nowMs * 0.01 + i * 2) * (radius * 0.25);
            const sy = baseOffsetY + p * plumeH * 1.3;
            ctx.fillStyle = i % 2 === 0 ? pal.highlight : "#ffffff";
            ctx.beginPath();
            ctx.arc(sx, sy, Math.max(1.5, radius * 0.04 * (1 - p)), 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (thruster === "sakura") {
        // Pétalos de flor de cerezo rosados cayendo y ondulando
        const petalColors = ["#f472b6", "#fb7185", "#fce7f3"];
        for (let i = 0; i < 6; i++) {
            const progress = ((nowMs * 0.002 + i * 0.166) % 1.0);
            const sway = Math.sin(nowMs * 0.004 + i * 1.7) * (radius * 0.45);
            const py = baseOffsetY + progress * (radius * 1.3);
            const px = sway;
            const rot = (nowMs * 0.003 + i);
            const pSize = Math.max(2, radius * 0.08 * (1 - progress * 0.3));

            ctx.save();
            ctx.translate(px, py);
            ctx.rotate(rot);
            ctx.fillStyle = petalColors[i % petalColors.length];
            ctx.globalAlpha = (1 - progress) * 0.9;
            ctx.beginPath();
            ctx.ellipse(0, 0, pSize, pSize * 0.55, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }
    } else if (thruster === "cosmic") {
        // Polvo cósmico violeta y estrellas centelleantes
        for (let i = 0; i < 6; i++) {
            const progress = ((nowMs * 0.0025 + i * 0.166) % 1.0);
            const spread = Math.sin(i * 3.7 + nowMs * 0.002) * (radius * 0.4);
            const py = baseOffsetY + progress * (radius * 1.35);
            const px = spread;
            const alpha = (1 - progress) * 0.9;

            ctx.fillStyle = i % 2 === 0 ? "#a855f7" : "#38bdf8";
            ctx.globalAlpha = alpha;
            ctx.beginPath();
            ctx.arc(px, py, Math.max(1.5, radius * 0.06 * (1 - progress * 0.5)), 0, Math.PI * 2);
            ctx.fill();

            if (i % 2 === 0) {
                ctx.strokeStyle = "#ffffff";
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(px - 3, py);
                ctx.lineTo(px + 3, py);
                ctx.moveTo(px, py - 3);
                ctx.lineTo(px, py + 3);
                ctx.stroke();
            }
        }
    } else if (thruster === "runic") {
        // Glifos rúnicos geométricos arcanos flotando
        const runes = ["᚛", "ᚠ", "ᚢ", "ᚦ", "ᚨ", "ᚱ"];
        ctx.font = `bold ${Math.max(8, radius * 0.22)}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        for (let i = 0; i < 4; i++) {
            const progress = ((nowMs * 0.002 + i * 0.25) % 1.0);
            const sway = Math.sin(nowMs * 0.003 + i * 2) * (radius * 0.35);
            const py = baseOffsetY + progress * (radius * 1.25);
            const px = sway;
            ctx.fillStyle = i % 2 === 0 ? "#fbbf24" : pal.secondary;
            ctx.globalAlpha = (1 - progress) * 0.95;
            ctx.fillText(runes[i % runes.length], px, py);
        }
    } else if (thruster === "bubbles") {
        // Burbujas prisma irisadas transparentes
        for (let i = 0; i < 5; i++) {
            const progress = ((nowMs * 0.0022 + i * 0.2) % 1.0);
            const bx = Math.sin(i * 2.5 + nowMs * 0.003) * (radius * 0.4);
            const by = baseOffsetY + progress * (radius * 1.3);
            const bR = Math.max(2, radius * 0.1 * (0.8 + 0.4 * Math.sin(i)));

            ctx.strokeStyle = "rgba(56, 189, 248, " + (1 - progress) * 0.85 + ")";
            ctx.fillStyle = "rgba(186, 230, 253, " + (1 - progress) * 0.35 + ")";
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.arc(bx, by, bR, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();

            ctx.fillStyle = "rgba(255, 255, 255, " + (1 - progress) * 0.9 + ")";
            ctx.beginPath();
            ctx.arc(bx - bR * 0.35, by - bR * 0.35, bR * 0.25, 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (thruster === "musical") {
        // Notas musicales de neón brillantes
        const notes = ["♪", "♫", "♬", "♩"];
        ctx.font = `bold ${Math.max(9, radius * 0.24)}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const noteColors = ["#38bdf8", "#f43f5e", "#facc15", "#a855f7"];
        for (let i = 0; i < 4; i++) {
            const progress = ((nowMs * 0.002 + i * 0.25) % 1.0);
            const nx = Math.sin(nowMs * 0.004 + i * 2.2) * (radius * 0.4);
            const ny = baseOffsetY + progress * (radius * 1.25);
            ctx.fillStyle = noteColors[i % noteColors.length];
            ctx.globalAlpha = (1 - progress) * 0.95;
            ctx.fillText(notes[i % notes.length], nx, ny);
        }
    } else if (thruster === "gold_coins") {
        // Monedas de oro giratorias
        for (let i = 0; i < 4; i++) {
            const progress = ((nowMs * 0.0024 + i * 0.25) % 1.0);
            const cxp = Math.sin(nowMs * 0.003 + i * 2) * (radius * 0.35);
            const cyp = baseOffsetY + progress * (radius * 1.3);
            const spin = Math.sin(nowMs * 0.008 + i);
            const cW = Math.max(2, radius * 0.09 * Math.abs(spin));
            const cH = Math.max(3, radius * 0.09);

            ctx.save();
            ctx.translate(cxp, cyp);
            ctx.fillStyle = "#facc15";
            ctx.strokeStyle = "#b45309";
            ctx.lineWidth = 1;
            ctx.globalAlpha = (1 - progress) * 0.95;
            ctx.beginPath();
            ctx.ellipse(0, 0, cW, cH, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.restore();
        }
    } else if (thruster === "ghost_fire") {
        // Flamas azul espectral / fuego fatuo
        for (let i = 0; i < 5; i++) {
            const progress = ((nowMs * 0.003 + i * 0.2) % 1.0);
            const fx = Math.sin(nowMs * 0.006 + i * 1.8) * (radius * 0.35);
            const fy = baseOffsetY + progress * (radius * 1.25);
            const fSize = Math.max(2, radius * 0.1 * (1 - progress * 0.6));

            ctx.fillStyle = i % 2 === 0 ? "#22d3ee" : "#818cf8";
            ctx.globalAlpha = (1 - progress) * 0.85;
            ctx.beginPath();
            ctx.arc(fx, fy, fSize, 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (thruster === "rainbow") {
        // Cinta arcoíris ondulante
        const colors = ["#ef4444", "#f97316", "#facc15", "#22c55e", "#38bdf8"];
        const wave = Math.sin(nowMs * 0.006) * (radius * 0.2);
        for (let c = 0; c < colors.length; c++) {
            const offX = (c - 2) * (radius * 0.08);
            ctx.strokeStyle = colors[c];
            ctx.lineWidth = Math.max(1.5, radius * 0.06);
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(offX, baseOffsetY);
            ctx.quadraticCurveTo(offX + wave, baseOffsetY + radius * 0.5, offX - wave, baseOffsetY + radius * 1.05);
            ctx.stroke();
        }
    }

    ctx.restore();
}

/** Dibuja un Close-Up grande y nítido de UN SOLO accesorio para los botones de selección del modal */
export function drawCosmeticItemThumbnail(
    ctx: CanvasRenderingContext2D,
    category: CosmeticCategory,
    itemId: string,
    w: number,
    h: number
): void {
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2;
    const cy = h / 2;

    if (itemId === "none" || (category === "eyes" && itemId === "default")) {
        const ballR = Math.floor(w * 0.44);
        // Icono gráfico elegante de "Desequipado / Ninguno / Reset" (⊘)
        ctx.strokeStyle = "#64748b";
        ctx.lineWidth = 2.4;
        ctx.beginPath();
        ctx.arc(cx, cy, ballR * 0.65, 0, Math.PI * 2);
        const slash = ballR * 0.46;
        ctx.moveTo(cx - slash, cy - slash);
        ctx.lineTo(cx + slash, cy + slash);
        ctx.stroke();

        ctx.strokeStyle = "rgba(148, 163, 184, 0.45)";
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(cx, cy, ballR * 0.8, 0, Math.PI * 2);
        ctx.stroke();
        return;
    }

    if (category !== "thruster") {
        const ballR = Math.floor(w * 0.44);
        // Círculo guía sutil de fondo solo para accesorios faciales, tatuajes y auras
        ctx.fillStyle = "rgba(30, 41, 59, 0.7)";
        ctx.beginPath();
        ctx.arc(cx, cy, ballR, 0, Math.PI * 2);
        ctx.fill();
    }

    if (category === "eyes") {
        drawFaceLayer(ctx, cx, cy + 1, 38, itemId as EyeStyle, "none", "none", 0, 0);
    } else if (category === "mouth") {
        drawFaceLayer(ctx, cx, cy - 8, 42, "default", "none", itemId as MouthStyle, 0, 0);
    } else if (category === "glasses") {
        drawFaceLayer(ctx, cx, cy + 2, 40, "default", itemId as GlassesStyle, "none", 0, 0);
    } else if (category === "tattoo") {
        drawTattooLayer(ctx, cx, cy, 36, itemId as TattooStyle, 1000);
    } else if (category === "aura") {
        const isSpikeOrNova = itemId === "spikes" || itemId === "phoenix" || itemId === "cosmic_nova" || itemId === "electric_storm";
        const yOffset = isSpikeOrNova ? cy : cy + 6;
        const rScale = 24;
        ctx.fillStyle = itemId === "phoenix" ? "rgba(245, 158, 11, 0.35)" : "rgba(56, 189, 248, 0.35)";
        ctx.beginPath();
        ctx.arc(cx, yOffset, rScale * 0.7, 0, Math.PI * 2);
        ctx.fill();
        drawAuraLayer(ctx, cx, yOffset, rScale, itemId as AuraStyle, 1200, "water");
    } else if (category === "thruster") {
        // Para propulsores: sin esfera interior para máxima visibilidad.
        // Centramos verticalmente las partículas en el centro del thumbnail (56x56).
        const previewRadius = 28;
        const thrusterOriginY = -14;
        drawThrusterPreview(ctx, cx, thrusterOriginY, previewRadius, itemId as ThrusterStyle, 1000, "water");
    }
}
